import React, { useEffect, useMemo, useState } from "react";
import {
    Alert, Badge, Button, Card, CardBody, Col, Container, Input, Row, Spinner, Table
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import * as dayjs from "dayjs";
import * as XLSX from "xlsx";
import Session from "../../auth/Session";
import { Roles } from "../../auth/Roles";
import useSortableData from "../useSortableData";
import SortableHeader from "../SortableHeader";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "");
const money = (n) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const days = (n) => {
    const fixed = Number(n ?? 0).toFixed(2);
    return fixed.replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
};

// Weekly paysheet reconciliation: every driver who ran a route this week alongside their paysheet
// status and pay, so finance can confirm no one who worked was missed and reconcile the total paid to
// QuickBooks.
const PaysheetReconciliationReport = () => {
    const navigate = useNavigate();
    const [weeks, setWeeks] = useState([{ endingDate: "", displayDate: "" }]);
    const [selectedWeek, setSelectedWeek] = useState("");
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [schedule, setSchedule] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const sortGetters = useMemo(() => ({
        daysWorked: (r) => Number(r.daysWorked ?? 0),
        paySheetAmount: (r) => Number(r.paySheetAmount ?? 0),
    }), []);

    const { items: sortedRows, sortConfig, requestSort } = useSortableData(
        rows,
        { key: "driverName", direction: "asc", getters: sortGetters }
    );

    useEffect(() => {
        if (!Session.hasRole(Roles.Admin)) {
            navigate("/accessdenied");
            return;
        }
        (async () => {
            const token = await Session.getToken();
            fetch("/api/routes/invoice-weeks", { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.ok ? r.json() : [])
                .then(list => {
                    setWeeks(list || []);
                    const preselect = (list || []).find(w => w.selected === true) || (list || [])[0];
                    if (preselect) setSelectedWeek(preselect.endingDate);
                })
                .catch(() => setWeeks([]));
        })();
    }, [navigate]);

    useEffect(() => {
        if (!selectedWeek) return;
        (async () => {
            setLoading(true);
            setError("");
            try {
                const token = await Session.getToken();
                const endingDate = dayjs(selectedWeek, "MM/DD/YYYY").format("YYYY-MM-DD");
                const res = await fetch(`/api/reports/reconciliation?endingDate=${endingDate}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
                const data = await res.json();
                setRows(data.rows || []);
                setSummary(data.summary || null);
                setSchedule(data.schedule || null);
            } catch (e) {
                setError(e.message || "Failed to load reconciliation report.");
                setRows([]);
                setSummary(null);
                setSchedule(null);
            } finally {
                setLoading(false);
            }
        })();
    }, [selectedWeek]);

    const exportToExcel = () => {
        const weekLabel = schedule ? `${fmtDate(schedule.startDate)} - ${fmtDate(schedule.endDate)}` : "";
        const header = ["Driver", "Worked", "Days Worked", "Paysheet Status", "Paysheet Total", "QuickBooks", "Needs Attention"];
        const body = sortedRows.map(r => [
            r.driverName,
            r.worked ? "Yes" : "No",
            r.worked ? Number(r.daysWorked ?? 0) : "",
            r.status,
            Number(r.paySheetAmount ?? 0),
            r.qbBillId ? "Synced" : (r.hasPaySheet && !r.approved ? "Not sent" : ""),
            r.needsAttention ? "YES" : ""
        ]);
        const summaryRows = summary ? [
            [],
            ["Drivers worked", summary.driversWorked],
            ["Paysheets", summary.paySheets],
            ["Approved", summary.approvedCount],
            ["Worked but not paid", summary.missingCount],
            ["Total approved paid", Number(summary.totalApprovedAmount ?? 0)],
        ] : [];
        const sheetData = [
            ["Reconciliation Report", "", "", weekLabel],
            [],
            header,
            ...body,
            ...summaryRows,
        ];
        const ws = XLSX.utils.aoa_to_sheet(sheetData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Reconciliation");
        const fileWeek = schedule ? dayjs(schedule.startDate).format("YYYY-MM-DD") : "week";
        XLSX.writeFile(wb, `reconciliation_${fileWeek}.xlsx`);
    };

    const statusBadge = (r) => {
        if (r.status === "Approved") return <Badge color="success">Approved</Badge>;
        if (r.status === "Submitted") return <Badge color="info">Submitted</Badge>;
        if (r.status === "In Review") return <Badge color="secondary">In Review</Badge>;
        if (r.status === "Voided") return <Badge color="warning">Voided</Badge>;
        return <Badge color="danger">No Paysheet</Badge>;
    };

    return (
        <Container className="mt-0">
            <Card>
                <CardBody>
                    <Row className="mb-3">
                        <Col><h5>Weekly Paysheet Reconciliation</h5></Col>
                        <Col className="text-end">
                            <Button size="sm" color="success" onClick={exportToExcel} disabled={loading || rows.length === 0}>
                                Export to Excel
                            </Button>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <Input
                                type="select"
                                value={selectedWeek}
                                onChange={(e) => setSelectedWeek(e.target.value)}
                            >
                                <option value="">-- Select Week --</option>
                                {weeks.map(w => (
                                    <option key={w.endingDate} value={w.endingDate}>{w.displayDate}</option>
                                ))}
                            </Input>
                        </Col>
                    </Row>

                    {error && <Alert color="danger">{error}</Alert>}

                    {summary && (
                        <Row className="mb-4">
                            <Col md={3}>
                                <Card className="shadow-sm"><CardBody>
                                    <div className="text-muted">Drivers Worked</div>
                                    <div className="h4 mb-0">{summary.driversWorked}</div>
                                </CardBody></Card>
                            </Col>
                            <Col md={3}>
                                <Card className="shadow-sm"><CardBody>
                                    <div className="text-muted">Approved Paysheets</div>
                                    <div className="h4 mb-0">{summary.approvedCount}</div>
                                </CardBody></Card>
                            </Col>
                            <Col md={3}>
                                <Card className={`shadow-sm ${summary.missingCount > 0 ? "border-danger" : ""}`}><CardBody>
                                    <div className="text-muted">Worked, Not Paid</div>
                                    <div className={`h4 mb-0 ${summary.missingCount > 0 ? "text-danger" : ""}`}>{summary.missingCount}</div>
                                </CardBody></Card>
                            </Col>
                            <Col md={3}>
                                <Card className="shadow-sm"><CardBody>
                                    <div className="text-muted">Total Approved Paid</div>
                                    <div className="h4 mb-0">${money(summary.totalApprovedAmount)}</div>
                                </CardBody></Card>
                            </Col>
                        </Row>
                    )}

                    {loading ? (
                        <div className="text-center py-4"><Spinner /></div>
                    ) : (
                        <Table bordered hover responsive>
                            <thead className="table-light">
                                <tr>
                                    <SortableHeader label="Driver" sortKey="driverName" sortConfig={sortConfig} onSort={requestSort} />
                                    <th>Worked</th>
                                    <SortableHeader label="Days Worked" sortKey="daysWorked" sortConfig={sortConfig} onSort={requestSort} style={{ textAlign: "right" }} />
                                    <SortableHeader label="Paysheet Status" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Paysheet Total" sortKey="paySheetAmount" sortConfig={sortConfig} onSort={requestSort} style={{ textAlign: "right" }} />
                                    <th>QuickBooks</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.length === 0 ? (
                                    <tr><td colSpan="7" className="text-center">No drivers or paysheets for the selected week.</td></tr>
                                ) : sortedRows.map(r => (
                                    <tr key={r.driverId} className={r.needsAttention ? "table-danger" : ""}>
                                        <td>{r.driverName}</td>
                                        <td className="text-center">{r.worked ? "Yes" : <span className="text-muted">No</span>}</td>
                                        <td style={{ textAlign: "right" }}>{r.worked ? days(r.daysWorked) : ""}</td>
                                        <td>{statusBadge(r)}</td>
                                        <td style={{ textAlign: "right" }}>{r.hasPaySheet ? `$${money(r.paySheetAmount)}` : ""}</td>
                                        <td className="text-center">
                                            {r.qbBillId
                                                ? <span className="text-success">Synced</span>
                                                : r.hasPaySheet && r.approved
                                                    ? <span className="text-muted">Not sent</span>
                                                    : <span className="text-muted">—</span>}
                                            {r.qbSyncError && <div className="text-danger small mt-1">{r.qbSyncError}</div>}
                                        </td>
                                        <td className="text-center">
                                            {r.needsAttention && <Badge color="danger">Not paid</Badge>}
                                            {r.hasPaySheet && (
                                                <Button size="sm" color="secondary" outline className="ms-2" onClick={() => navigate(`/reports/paysheet-view/${r.paySheetId}`)}>
                                                    View
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>
        </Container>
    );
};

export default PaysheetReconciliationReport;
